import { useState, useEffect, useCallback } from 'react';
import { transactionService } from '../../services/transactionService';
import { productService } from '../../services/productService';
import { shiftService } from '../../services/shiftService';
import { productReportsService } from '../../services/productReportsService';

/**
 * Shared hook for loading report data across report components.
 * Each report component can call the specific loader(s) it needs.
 */
export const useReportData = (locationId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Core data
  const [dailyStats, setDailyStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [lowMarginProducts, setLowMarginProducts] = useState([]);

  // Product reports data
  const [highestRotationProducts, setHighestRotationProducts] = useState([]);
  const [lowestRotationProducts, setLowestRotationProducts] = useState([]);
  const [bestsellingProducts, setBestsellingProducts] = useState([]);
  const [salesForecast, setSalesForecast] = useState([]);
  const [productsSummary, setProductsSummary] = useState(null);
  const [warehouseStock, setWarehouseStock] = useState(null);

  // Margin reports
  const [marginReports, setMarginReports] = useState({
    daily: {},
    monthly: [],
    daily_history: [],
    summary: {}
  });

  const loadDailyStats = useCallback(async () => {
    try {
      const stats = await transactionService.getDailyStats(locationId);
      setDailyStats(stats);
    } catch (err) {
      console.error('Blad podczas pobierania statystyk dziennych:', err);
    }
  }, [locationId]);

  const loadMonthlyStats = useCallback(async () => {
    try {
      const stats = await transactionService.getMonthlyStats(locationId);
      setMonthlyStats(stats);
    } catch (err) {
      console.error('Blad podczas pobierania statystyk miesiecznych:', err);
    }
  }, [locationId]);

  const loadTopProducts = useCallback(async () => {
    try {
      // Top produkty = realni bestsellerzy wg ilosci sprzedanej (per lokalizacja),
      // a NIE pierwsze produkty z katalogu. Endpoint zwraca juz posortowana liste.
      const response = await productReportsService.getBestsellingProducts({
        limit: 10,
        metric: 'quantity',
        location_id: locationId
      });
      const products = response?.data?.products || [];
      setTopProducts(products);
    } catch (err) {
      console.error('Blad podczas pobierania top produktow:', err);
    }
  }, [locationId]);

  const loadShiftHistory = useCallback(async () => {
    try {
      const response = await shiftService.getShiftsHistory({ location_id: locationId });
      const data = response?.data || [];
      setShiftHistory(data);
    } catch (err) {
      console.error('Blad podczas pobierania historii zmian:', err);
    }
  }, [locationId]);

  const loadLowMarginProducts = useCallback(async () => {
    try {
      // Koszt FIFO per-lokalizacja: te same produkty moga miec inna marze w roznych
      // magazynach (rozne ceny zakupu), wiec przekazujemy wybrana lokalizacje.
      const response = await productService.getLowMarginProducts(15, locationId);
      const data = response?.data || [];
      setLowMarginProducts(data);
    } catch (err) {
      console.error('Blad podczas pobierania produktow z niska marza:', err);
    }
  }, [locationId]);

  const loadMarginReports = useCallback(async () => {
    try {
      const response = await transactionService.getMarginReports(locationId);
      if (response.success && response.data) {
        setMarginReports(response.data);
      }
    } catch (err) {
      console.error('Blad podczas pobierania raportow marzy:', err);
    }
  }, [locationId]);

  const loadHighestRotationProducts = useCallback(async (reportParams) => {
    try {
      const response = await productReportsService.getHighestRotationProducts({ ...reportParams, location_id: locationId });
      setHighestRotationProducts(response.data || []);
    } catch (err) {
      console.error('Blad podczas pobierania najbardziej rotujacych produktow:', err);
    }
  }, [locationId]);

  const loadLowestRotationProducts = useCallback(async (reportParams) => {
    try {
      const response = await productReportsService.getLowestRotationProducts({ ...reportParams, location_id: locationId });
      setLowestRotationProducts(response.data || []);
    } catch (err) {
      console.error('Blad podczas pobierania najmniej rotujacych produktow:', err);
    }
  }, [locationId]);

  const loadBestsellingProducts = useCallback(async (reportParams) => {
    try {
      const response = await productReportsService.getBestsellingProducts({ ...reportParams, location_id: locationId });
      setBestsellingProducts(response.data || {});
    } catch (err) {
      console.error('Blad podczas pobierania bestsellerow:', err);
    }
  }, [locationId]);

  const loadSalesForecast = useCallback(async (reportParams) => {
    try {
      const response = await productReportsService.getSalesForecast({ ...reportParams, location_id: locationId });
      setSalesForecast(response.data || {});
    } catch (err) {
      console.error('Blad podczas pobierania prognozy sprzedazy:', err);
    }
  }, [locationId]);

  const loadProductsSummary = useCallback(async (reportParams) => {
    try {
      const response = await productReportsService.getProductsSummary({ ...reportParams, location_id: locationId });
      setProductsSummary(response.data || {});
    } catch (err) {
      console.error('Blad podczas pobierania podsumowania produktow:', err);
    }
  }, [locationId]);

  const loadWarehouseStock = useCallback(async () => {
    console.log('[DEBUG] loadWarehouseStock called with locationId:', locationId);
    try {
      const response = await productReportsService.getWarehouseStockReport({ location_id: locationId });
      console.log('[DEBUG] warehouse-stock response:', response);
      setWarehouseStock(response.data || null);
    } catch (err) {
      console.error('Blad podczas pobierania raportu magazynu:', err);
    }
  }, [locationId]);

  const loadProductReports = useCallback(async (reportParams) => {
    try {
      await Promise.all([
        loadHighestRotationProducts(reportParams),
        loadLowestRotationProducts(reportParams),
        loadBestsellingProducts(reportParams),
        loadSalesForecast(reportParams),
        loadProductsSummary(reportParams),
        loadWarehouseStock()
      ]);
    } catch (err) {
      console.error('Blad podczas ladowania raportow produktowych:', err);
    }
  }, [loadHighestRotationProducts, loadLowestRotationProducts, loadBestsellingProducts, loadSalesForecast, loadProductsSummary, loadWarehouseStock]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDailyStats(),
        loadMonthlyStats(),
        loadTopProducts(),
        loadShiftHistory(),
        loadLowMarginProducts(),
        loadMarginReports(),
        loadProductReports()
      ]);
    } catch (err) {
      setError('Blad podczas ladowania danych: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [loadDailyStats, loadMonthlyStats, loadTopProducts, loadShiftHistory, loadLowMarginProducts, loadMarginReports, loadProductReports]);

  useEffect(() => {
    loadInitialData();
  }, [locationId]);

  return {
    loading,
    error,
    setError,
    // Data
    dailyStats,
    monthlyStats,
    topProducts,
    shiftHistory,
    lowMarginProducts,
    highestRotationProducts,
    lowestRotationProducts,
    bestsellingProducts,
    salesForecast,
    productsSummary,
    warehouseStock,
    marginReports,
    // Loaders
    loadInitialData,
    loadDailyStats,
    loadMonthlyStats,
    loadTopProducts,
    loadShiftHistory,
    loadLowMarginProducts,
    loadMarginReports,
    loadHighestRotationProducts,
    loadLowestRotationProducts,
    loadBestsellingProducts,
    loadSalesForecast,
    loadProductsSummary,
    loadWarehouseStock,
    loadProductReports,
  };
};
